/**
 * Minimal JWT payload reader — decodes a token's claims **without verifying its
 * signature**. Only ever used to read a value our own backend embedded in a
 * short-lived token (e.g. the `email` in a `mfaToken`); never trust the result
 * for authorization decisions.
 */

/** Decode a JWT's payload segment, or `null` if it isn't a well-formed JWT. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  // Browser-only (`atob`); on the server there is nothing to decode against.
  if (typeof atob !== 'function') return null

  const segment = token.split('.')[1]
  if (!segment) return null

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    // Percent-decode so multi-byte UTF-8 claims survive the `atob` round-trip.
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((char) => '%' + char.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    const parsed = JSON.parse(json)
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/** Read the `email` claim from a JWT, or `null` when absent/unreadable. */
export function decodeJwtEmail(token: string): string | null {
  const email = decodeJwtPayload(token)?.email
  return typeof email === 'string' ? email : null
}
